import ResourceTyped from 'Resources/ResourceTyped';

export type AgentApprovalStatus = 'pending' | 'approved' | 'rejected' | 'changes_requested' | 'expired';

export type AgentApprovalResultPayload = {
	kind: 'crmMutation' | 'sendEmail' | 'multiAction';
	entity?: string | null;
	id?: number | null;
	urlPath?: string | null;
	action?: string | null;
	message?: string | null;
	success?: boolean;
	task?: string | null;
	ranInline?: boolean;
	totalChanged?: number | null;
	total?: number | null;
	notificationId?: number | null;
} & Record<string, unknown>;

export type AgentApproval = {
	id: number;
	runId: number;
	agentId: number;
	immediateTargetAgentId: number | null;
	immediateTargetUserId: number | null;
	action: string;
	actionPayload: Record<string, unknown> | null;
	modifiedPayload: Record<string, unknown> | null;
	status: AgentApprovalStatus;
	resolvedByUserId: number | null;
	resolvedByAgentId: number | null;
	resolution: string | null;
	regDate: string;
	resolvedAt: string | null;
	expiresAt: string;
	resultEntityType: string | null;
	resultEntityId: number | null;
	resultPayload: AgentApprovalResultPayload | null;
};

// The `action_payload`, `modified_payload` and `result_payload` columns are
// stored as JSON strings (see approvalRouter.createApprovalRequest / resolve
// — they all `JSON.stringify` before saving). The API returns the rows as-is,
// so the resource is the right seam to parse once and hand consumers real
// objects matching the declared types.
const parseJsonField = (raw: unknown): Record<string, unknown> | null => {
	if (raw == null) return null;
	if (typeof raw === 'string') {
		try {
			return JSON.parse(raw) as Record<string, unknown>;
		} catch {
			return null;
		}
	}
	if (typeof raw === 'object') return raw as Record<string, unknown>;
	return null;
};

export const normaliseApproval = (a: AgentApproval): AgentApproval => ({
	...a,
	actionPayload: parseJsonField(a.actionPayload),
	modifiedPayload: parseJsonField(a.modifiedPayload),
	resultPayload: parseJsonField(a.resultPayload) as AgentApprovalResultPayload | null
});

class AgentApprovals extends ResourceTyped<AgentApproval> {
	constructor() {
		super('agent-approvals');
	}

	async list(
		filters: {
			status?: AgentApprovalStatus;
			limit?: number;
			offset?: number;
			adminQueue?: boolean;
			clientId?: number;
		} = {}
	): Promise<AgentApproval[]> {
		const params = new URLSearchParams();
		if (filters.status) params.set('status', filters.status);
		if (filters.limit != null) params.set('limit', String(filters.limit));
		if (filters.offset != null) params.set('offset', String(filters.offset));
		if (filters.adminQueue) params.set('adminQueue', 'true');
		if (filters.clientId != null) params.set('clientId', String(filters.clientId));
		const q = params.toString();
		const res = await fetch(`/api/v2/agent-approvals${q ? `?${q}` : ''}`);
		if (!res.ok) throw new Error(`Failed to list approvals (${res.status})`);
		const body = await res.json();
		return (body.data ?? []).map(normaliseApproval);
	}

	async getOne(id: number): Promise<AgentApproval> {
		const res = await fetch(`/api/v2/agent-approvals/${id}`);
		if (!res.ok) throw new Error(`Failed to load approval (${res.status})`);
		const body = await res.json();
		return normaliseApproval(body.data);
	}

	async getMany(ids: number[]): Promise<AgentApproval[]> {
		if (!ids.length) return [];
		const settled = await Promise.all(
			ids.map(id =>
				this.getOne(id).then(
					a => ({ ok: true as const, a }),
					() => ({ ok: false as const, a: null })
				)
			)
		);
		return settled.filter((r): r is { ok: true; a: AgentApproval } => r.ok).map(r => r.a);
	}

	async approve(
		id: number,
		resolution?: string,
		modifiedPayload?: Record<string, unknown>,
		opts?: { skipContinuation?: boolean; externalResult?: { entityType: string; entityId: number } }
	): Promise<{ approval: AgentApproval; executionResult: unknown }> {
		return this.resolve(id, 'approve', resolution, modifiedPayload, opts);
	}

	async reject(
		id: number,
		resolution: string,
		modifiedPayload?: Record<string, unknown>,
		opts?: { skipContinuation?: boolean }
	): Promise<{ approval: AgentApproval; executionResult: unknown }> {
		return this.resolve(id, 'reject', resolution, modifiedPayload, opts);
	}

	async requestChanges(
		id: number,
		resolution: string
	): Promise<{ approval: AgentApproval; executionResult: unknown }> {
		return this.resolve(id, 'request-changes', resolution);
	}

	async retry(id: number): Promise<{ approval: AgentApproval; executionResult: unknown }> {
		const res = await fetch(`/api/v2/agent-approvals/${id}/retry`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: '{}'
		});
		if (!res.ok) {
			const body = await res.json().catch(() => null);
			throw new Error(body?.error?.translated || body?.error?.msg || `Failed to retry (${res.status})`);
		}
		const body = await res.json();
		return { approval: normaliseApproval(body.data.approval), executionResult: body.data.executionResult };
	}

	private async resolve(
		id: number,
		action: 'approve' | 'reject' | 'request-changes',
		resolution?: string,
		modifiedPayload?: Record<string, unknown>,
		opts?: { skipContinuation?: boolean; externalResult?: { entityType: string; entityId: number } }
	): Promise<{ approval: AgentApproval; executionResult: unknown }> {
		const payload: Record<string, unknown> = {};
		if (resolution) payload.resolution = resolution;
		if (modifiedPayload) payload.modifiedPayload = modifiedPayload;
		if (opts?.skipContinuation) payload.skipContinuation = true;
		// The record already exists — the user saved it in the entity's own form.
		// Resolves the row against that id instead of running the executor.
		if (opts?.externalResult) payload.externalResult = opts.externalResult;
		const res = await fetch(`/api/v2/agent-approvals/${id}/${action}`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload)
		});
		if (!res.ok) {
			const body = await res.json().catch(() => null);
			throw new Error(body?.error?.translated || body?.error?.msg || `Failed to ${action} (${res.status})`);
		}
		const body = await res.json();
		return { approval: normaliseApproval(body.data.approval), executionResult: body.data.executionResult };
	}
}

const resource = new AgentApprovals();
export default resource;
